import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Index from "@/pages/Index";
import TrackOrder from "@/pages/TrackOrder";
import ServiceablePincode from "@/pages/ServiceablePincode";
import PrintShippingLabel from "@/pages/PrintShippingLabel";
import CODPage from "@/pages/CODPage";
import Blogs from "@/pages/Blogs";
import BlogDetails from "@/pages/BlogDetails";
import Resources from "@/pages/Resources";
import ContactUs from "@/pages/ContactUs";
import Pricing from "@/pages/Pricing";
import ReturnRefund from "@/pages/ReturnRefund";
import TermsConditions from "@/pages/TermsConditions";
import ShippingPolicy from "@/pages/ShippingPolicy";
import Policy from "@/pages/Policy";
import AboutUs from "@/pages/AboutUs";
import HelpCenter from "@/pages/HelpCenter";
import ProductDomesticShipping from "@/pages/ProductDomesticShipping";
import ProductCargoShipping from "@/pages/ProductCargoShipping";
import ProductInternationalShipping from "@/pages/ProductInternationalShipping";
import ProductEngagePlus from "@/pages/ProductEngagePlus";
import ProductRTOPredict from "@/pages/ProductRTOPredict";
import Maintenance from "@/pages/Maintenance";
import NotFound from "@/pages/NotFound";
import GlobalStructuredData from "@/components/GlobalStructuredData";
import { LEGACY_PHP_REDIRECTS, ROUTES } from "@/config/routes";

/** Must be a Fragment of <Route>s — <Routes> only accepts Route or Fragment children. */
const legacyPhpRedirectRoutes = (
  <>
    {Object.entries(LEGACY_PHP_REDIRECTS).map(([legacyPath, canonicalPath]) => (
      <Route key={legacyPath} path={legacyPath} element={<Navigate to={canonicalPath} replace />} />
    ))}
  </>
);

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const sectionId = decodeURIComponent(hash.slice(1));
      const scrollToSection = () => {
        const section = document.getElementById(sectionId);
        if (!section) return false;
        section.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      };

      if (scrollToSection()) return;

      const timeoutId = window.setTimeout(() => {
        scrollToSection();
      }, 100);

      return () => window.clearTimeout(timeoutId);
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

// Toggle with REACT_APP_MAINTENANCE_MODE=true at build/start time.
// When enabled, every route serves the Maintenance page. When disabled,
// the `/maintenance` path is not exposed and redirects to home.
const MAINTENANCE_MODE =
  String(process.env.REACT_APP_MAINTENANCE_MODE).toLowerCase() === "true";

function App() {
  if (MAINTENANCE_MODE) {
    return (
      <div className="App">
        <BrowserRouter>
          <GlobalStructuredData />
          <ScrollManager />
          <Routes>
            {legacyPhpRedirectRoutes}
            <Route path="*" element={<Maintenance />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <GlobalStructuredData />
        <ScrollManager />
        <Routes>
          {legacyPhpRedirectRoutes}
          <Route path={ROUTES.HOME} element={<Index />} />
          <Route path={ROUTES.TRACK_ORDER} element={<TrackOrder />} />
          <Route path="/serviceable-pincode" element={<ServiceablePincode />} />
          <Route path="/print-label" element={<PrintShippingLabel />} />
          <Route path="/cod" element={<CODPage />} />
          <Route path={ROUTES.BLOGS} element={<Blogs />} />
          <Route path="/blogs/:slug" element={<BlogDetails />} />
          <Route path={ROUTES.RESOURCES} element={<Resources />} />
          <Route path={ROUTES.CONTACT} element={<ContactUs />} />
          <Route path={ROUTES.PRICING} element={<Pricing />} />
          <Route path="/return-refund" element={<ReturnRefund />} />
          <Route path={ROUTES.TERMS} element={<TermsConditions />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/policy" element={<Policy />} />
          <Route path={ROUTES.ABOUT} element={<AboutUs />} />
          <Route path={ROUTES.HELP_CENTER} element={<HelpCenter />} />
          <Route path="/products/domestic-shipping" element={<ProductDomesticShipping />} />
          <Route path="/products/cargo-shipping" element={<ProductCargoShipping />} />
          <Route path="/products/international-shipping" element={<ProductInternationalShipping />} />
          <Route path="/products/engage-plus" element={<ProductEngagePlus />} />
          <Route path="/products/rto-predict" element={<ProductRTOPredict />} />
          <Route path="/maintenance" element={<Navigate to="/" replace />} />
          {/* Catch-all 404. Page sets robots="noindex, follow" so crawlers
              skip it but still follow the links back into the site. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;